import type { CalendarFacts } from './calendar-catchup.js';
// Browser clock and native OoC-round decisions. The adapter supplies held rows and the page's real roll source;
// native turn ticks dry-run each effect before drawing. PHP checks authority/CAS, then saves and relays the plan.
// Unsupported boundary effects are named GM skips; the clock and independent supported families still advance.
import { ftCampaignTimeAddSeconds, ftCampaignTimeSubtractSeconds, type CampaignCalendarEmbed, type CampaignClockCols } from '../../shared/campaign-clock.js';
import { layerListEntryUuid } from '../../shared/array-types.js';
import { CAMPAIGN_HANDLED_STATUS_IDS, DOT_ROLL_TYPE, expirableStatuses, STATUS_IDS, statusEndSeconds, statusItemId, statusPaused, statusRows, type ExpiryReaders, type StatusRemoval } from '../statuses/expiry.js';
import type { AbilityEntry, EndTurnReaders } from './end-turn.js';
import { skillsWithoutStatuses, type EndTurnRowFrame } from './status-frame.js';
import { skipUnported } from '../unported.js';
import { ecLean, HandBack, itemField, type LogRow } from '../ability/context.js';
import { masteryOutOfCombatDecision } from './k4-upkeep.js';
import { resolveOocRoundPass, resolveOocRoundPassAsync, type OocRoundInput, type OocRoundResult } from './ooc-round-pass.js';
import type { TurnTickResult } from './ticks.js';
import { chargeReductionDelta, type ReductionReaders } from '../resources/reduction-guard.js';
import { timeStatusProjection, timeSavePlan, type TimeSavePlan, type TimeStatusProjection } from './time-save-plan.js';
import { resolveHungerConsequences, type HungerStatusEvent, type HungerConsequencePlan } from './hunger-consequences.js';
import { withInventoryExpiry, inventoryExpiryPlan } from '../statuses/inventory-expiry.js';
import { mergeSpawnPlans } from '../spawn/row-plan.js';

export const TIME_PUSH_SCHEMA = 'janus.time.push.v1' as const;
export const TIME_PUSH_RULES_COMBAT = 't4a-2026-09-10' as const;
/** R-214: old skip-only pages cannot retire a boundary after the browser round-pass cutover. */
export const TIME_PUSH_RULES_CALENDAR = 'ooc-round-2026-10-15' as const;
/** @deprecated the combat lane's rules string; kept for the T4a smoke's import surface. */
export const TIME_PUSH_RULES = TIME_PUSH_RULES_COMBAT;
export const TIME_PUSH_ROUND_SECONDS = 6;
export const TIME_PUSH_MAX_ROUNDS = 14400; // 24 h; the widget's own cap is far lower

/** PlayableClass / ClassMechanic catalog ids (includes/additions.php) the out-of-combat pass 3 keys on. */
export const CLASS_IDS = { psion: 'F2D31321-42E4-926B-892A-DF0A41176D14', bard: 'D656F798-435B-26F9-0332-0E489BBDEC7F', wizard: '2BCF5CBC-1E87-E654-8310-E56EC051E559', summoner: '35EDCE49-85F9-A36E-4407-547E0153DA05' } as const;
export const MECHANIC_IDS = { psi: '6A8183E1-349E-3960-B465-6C863097E193', melody: 'BD233002-F6C0-A136-6EE4-C899A9E92856', mastery: 'B56929A7-6BB8-81FA-BFF8-2CE4B0812030' } as const;
/** The embed's per-PC reasons the T4c ticks cover on a HELD player character (everything else stays a gate). */
export const TICK_COVERED_PC_REASONS = new Set(['psion', 'bard', 'wizard']);
/** StatusEffect catalog ids the hunger/thirst tick keys on (check_hunger_and_thirst_mechanics; resolved on staging 2026-09-11). */
export const HUNGER_IDS = { tick: '88F8F0BA-3E96-4D9C-AFCE-008D849B0BFC', hungry: '5672B433-B59D-561E-B4C6-79565C46B3CA', thirsty: '6FAF0CD0-26E2-60A3-0608-A3C8E1828027', starving: '7780B669-28AA-680E-D28D-397B05A15FC6', parched: 'C1F4E2B6-DA45-8CAC-322D-C5DF54FFC418', exhausted: 'EB6FB558-7427-A647-8F46-D2E8C25F1F60', maimed: 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D' } as const;
export const HUNGER_TICK_SECONDS = 3600;
/** SpeedType catalog ids (includes/additions.php): a zero chain on a flying / swimming PC runs apply_status_effect's fall / sink check
 * in legacy — a gate (the endpoint judged it until R-122). */
export const AIRBORNE_SPEED_IDS = new Set(['F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4', 'ED9810A9-3456-4D6B-BC5C-A1BAF5952B98']);
const SPEED_FLYING_ID = 'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4';
/** The statuses a hunger step can apply — the adapter holds their definitions for a flying / swimming PC (R-129 airborneCheck). */
export const HUNGER_APPLY_IDS = [HUNGER_IDS.hungry, HUNGER_IDS.thirsty, HUNGER_IDS.starving, HUNGER_IDS.parched, HUNGER_IDS.maimed, HUNGER_IDS.exhausted] as const;
/** The families legacy's out-of-combat pass 2 hands to a SPECIAL handler (never a plain removal):
 * crafting, Trapped, the two contingency arming items, Suffocating / Drowning. A due one skips its effect.
 * Resting stays held for the time sender's browser long-rest completion after the saved clock. */
export const SPECIAL_STATUS_IDS = new Set<string>([STATUS_IDS.crafting, '78C51D2D-5178-551C-1249-B5DDA4083098' /* trapped */,
  'DC9778C6-5B9B-1501-B86A-5EEF4671F66A' /* sanguine contingency */, '3BDE1AA4-23D6-C4E8-CADD-ED15B4403EA6' /* spell contingency */, STATUS_IDS.drowning, STATUS_IDS.suffocating]);
/** janus_trigger_carriers() — the ability buckets janus_trigger() walks for a status gain / loss trigger. */
const TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** computed.campaign.heartbeat as the clock runtime holds it (campaign_heartbeat_inc.php). */
export interface HeartbeatEmbedLike {
  timed?: Array<{ id: string; target: string; end: number }>;
  crafting_wip?: boolean;
  hunger?: boolean;
  pcs?: Record<string, string[]>;
  encounter?: { id: string; in_combat?: boolean; reasons: string[] } | null;
  timed_error?: string;
}

/** Deterministic pass-3 charge accrual on a held player character; PHP CASes and saves it. */
export interface ChargeTick { actor_id: string; kind: 'psi' | 'melody' | 'mastery'; item_id: string; from: number; to: number }
export interface TimePushRowFrame extends EndTurnRowFrame { charges: Array<{ item_id: string; current: number }>; meters?: { hunger_meter_current: number; thirst_meter_current: number }; statuses_upsert?: Row[]; status_projection?: TimeStatusProjection }
/** The hourly meter decrement and private one-hour marker on one held PC. */
export interface MeterTick { actor_id: string; hunger_from: number; hunger_to: number; thirst_from: number; thirst_to: number; tick: { status_id: string; connected_id: string; starts: CampaignClockCols; ends: CampaignClockCols } }
/** A <= 20 % hunger/thirst threshold: self-cast Hungry/Thirsty at its hourly onset, with no end. */
export interface ThresholdApply { actor_id: string; kind: 'hungry' | 'thirsty'; status_item_id: string; status_id: string; connected_id: string; starts: CampaignClockCols }
/** T4e (2026-09-28, CMP2 + TIME): the ZERO transition on one held player character — legacy removes Hungry / Thirsty, then applies
 * Starving / Parched (1 day), Maimed and Exhausted, self-cast, in that order. Maimed (skill_rolls_mod) and Exhausted (skill_rolls_dis)
 * carry compiled footprints. The browser supplies each new row's persistent identity and settles its consequences on held state. */
export interface ZeroTransition { actor_id: string; meter: 'hunger' | 'thirst'; remove_item_id: string; remove_status_ids: string[]; apply_item_ids: string[];
  row_ids?: Record<string, { status_id: string; connected_id: string }>;
  /** The first apply (Starving / Parched) ends one day after its onset; the rest never end. */
  first_ends: CampaignClockCols;
  /** An unheld flag is gained and lost in the same pass, including both listener events. */
  flag_transient?: true;
  /** T4e+: Maimed already held (a quantity family, cap 9) — legacy adds ONE stack to that row instead of minting one; the stack scales
   *  the skill_rolls_mod residue (2026-09-28 fix). `apply_item_ids` then omits Maimed. The 10th stack (overflow = Dead) is a gate. */
  maimed_stack?: { status_id: string; from: number } }
export const MAIMED_MAX_STACK = 9;
/** T4f (2026-09-28): the "to death" branch — the meter reached zero while Exhausted is held (it already was, or the OTHER meter's zero
 * chain applied it this pass: both meters at zero). Legacy casts the free ability (a Fortitude contest; Maimed on a failure) and then
 * re-applies Starving / Parched for 1 day. The push commits the re-apply (`reapplies`, a zero-layer row); the adapter then casts the
 * ability through the client-truth ability lane (window.ftTryCommitAbilityUse). R-100 (2026-10-01): the legacy `to_death_cast` fallback is
 * deleted — a lane hand-back is reported and warned, never cast in PHP. */
export interface ToDeath { actor_id: string; meter: 'hunger' | 'thirst'; ability_id: string }
export interface Reapply { actor_id: string; kind: 'starving' | 'parched'; status_item_id: string; status_id: string; connected_id: string; starts: CampaignClockCols; ends: CampaignClockCols }
export const TO_DEATH_IDS = { hunger: '152FE656-CAC1-C5E1-7800-F17E12138E20', thirst: '3A1FBDC5-FC85-7C4B-EEBF-663EECEB641C' } as const;

export type TimePushReaders = ExpiryReaders & Partial<EndTurnReaders>;

export interface TimePushInput {
  calendarFacts?: CalendarFacts;
  weariness?: boolean;
  campaignId: string;
  encounterId: string;
  rounds: number;
  rollPrivate: number;
  gmLog: boolean;
  /** Legacy Fast Forward jumps the span and runs one sweep; ordinary pushes accrue every round. */
  fastForward?: boolean;
  isGM: boolean;
  /** The loaded encounter's combat flag as the browser holds it; null when unknown. */
  inCombat: boolean | null;
  /** The browser's STORED clock snapshot (pre-fold) and its running anchor, verbatim from the campaign arr. */
  clock: CampaignClockCols | null;
  runningSince: string | null;
  calendar: CampaignCalendarEmbed | null;
  /** T4b/T4c inputs: the heartbeat embed, the roster rows this browser holds, and the readers over them (nowSeconds =
   * the DERIVED clock as the tab shows it; abilityEntries = the held row's charged entries). Absent out of combat = a gate. */
  heartbeat?: HeartbeatEmbedLike | null;
  rows?: Row[];
  encounter?: Row;
  roundReaders?: (scene: Row, clock: CampaignClockCols) => EndTurnReaders;
  roundPreflightOnly?: boolean;
  readers?: TimePushReaders | null;
  /** T4c-2: the campaign's hunger/thirst switch and the DERIVED clock (what the tab shows) the tick stamps are computed from. */
  hungerEnabled?: boolean;
  derivedClock?: CampaignClockCols | null;
  /** The RUNNING clock's hunger heartbeat: rounds 0, out of combat only, carrying the hunger family.
   * Conserved encounter rounds use their own queued native save; calendar/player residue remains OOC-TIME-2. */
  hungerOnly?: boolean;
  /** R-100 / R-129 (lane A4, 2026-10-01): PCs whose hunger step the ADAPTER withholds after a pre-commit proof failed (an owed "to death"
   *  cast the ability lane would hand back — there is no PHP fallback), keyed by actor id → the withheld reason. */
  withheldHunger?: Record<string, string>;
}

export interface TimePushCommand {
  schema: typeof TIME_PUSH_SCHEMA;
  operation_id: string;
  campaign_id: string;
  encounter_id: string;
  mode: 'combat' | 'calendar';
  rounds: number;
  seconds: number;
  before: { clock: CampaignClockCols; running_since: string | null; in_combat: boolean; campaign_light?: string | null; campaign_sun_state?: string | null };
  expected: { clock: CampaignClockCols; campaign_light?: string | null; campaign_sun_state?: string | null; sun_transition_minutes?: [number, number, number, number] | null };
  /** T4b: the plain calendar removals (T1's StatusRemoval shape, log_by = the caster as legacy's pass 2 logs it). */
  removals: StatusRemoval[];
  /** T4c: the deterministic charge ticks on held player characters. */
  ticks: ChargeTick[];
  /** T4c-2: the hourly hunger/thirst meter ticks on held player characters. */
  meters: MeterTick[];
  /** T4d: the <= 20 % Hungry / Thirsty applies on held player characters (omitted when empty). */
  thresholds?: ThresholdApply[];
  /** Zero transitions for every eligible held PC, with explicit saved row identities; native consequences follow. */
  zeros?: ZeroTransition[];
  /** T4f: the "to death" casts owed after the commit (omitted when none) + the Starving / Parched re-applies the push commits. */
  to_deaths?: ToDeath[];
  reapplies?: Reapply[];
  /** Per touched row, what the cache patch applies (status removals + the skills reversal + charges) — the frame every tab applies. */
  frame: { rows: Record<string, TimePushRowFrame> };
  /** Every roster row this browser holds — the endpoint refuses a due row / a pass-3 tick on any OTHER live target. */
  held_actor_ids: string[];
  roll_private: number;
  gm_log: boolean;
  log_seconds?: number;
  /** Saved with the clock; each refused member retains its own original interval. */
  member_intervals?: import('./member-time-debt.js').MemberTimeInterval[];
  member_queue_before?: unknown;
  rules_version: typeof TIME_PUSH_RULES_COMBAT | typeof TIME_PUSH_RULES_CALENDAR;
  /** GM-only verdicts for effects omitted at this clock boundary. */
  rows?: LogRow[];
  tick?: TurnTickResult;
  tick_before_ec?: Record<string, Record<string, number | string | null>>;
  /** Consequences settle after the primary hunger rows, in the same saved operation. */
  hunger_tick?: TurnTickResult;
  hunger_tick_before_ec?: Record<string, Record<string, number | string | null>>;
  /** A running span folded with a maximum-size push may exceed one native batch. Persist its remaining browser debt. */
  round_debt?: number;
  time_save?: TimeSavePlan;
}

export type TimePushScope =
  /** `hunger_withheld` (t4g, Codex review 2026-10-01): PCs whose hunger step this command leaves out — a case no browser resolver serves
   *  yet (a missing support definition or an unsupported status listener). The rest
   *  of the push commits; the withheld PC is decided again on the next push / heartbeat (board R-129). */
  | { supported: true; command: TimePushCommand; hunger_withheld?: string[] }
  | { supported: false; reason: string; detail?: Record<string, unknown> };

const isClock = (value: CampaignClockCols | null): value is CampaignClockCols =>
  !!value && ['year', 'month', 'day', 'hour', 'minute', 'second'].every(key => Number.isInteger((value as unknown as Record<string, unknown>)[key]));

const flag = (status: Row, key: string): boolean => Number(scalar(status[key]) ?? 0) !== 0;
const rowId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id);
const itemTypeOf = (row: Row): string => String(scalar(record(row.items)?.item_type) ?? scalar(record(row.encounter_creatures)?.item_type) ?? scalar(row.item_type) ?? '').toLowerCase();
const isDead = (row: Row): boolean => Number(scalar(record(row.encounter_creatures)?.encounter_creature_dead) ?? 0) !== 0;
/** player_characters.class_1..20 as the compiled PC row carries them. */
export function classIdsOf(row: Row): string[] {
  const pc = record(row.player_characters);
  const out: string[] = [];
  for (let x = 1; x <= 20; x += 1) { const id = upper(scalar(pc?.['class_' + x])); if (id) out.push(id); }
  return out;
}
const hasStatus = (row: Row, itemId: string): boolean => statusRows(row, 'target').some(s => statusItemId(s) === itemId);

/** Pass-1 reasons for the loaded encounter, mirroring ft_hb_member_reasons() + the pAOE test in ft_campaign_heartbeat_embed():
 * any live member whose end-of-round pass would WRITE (dying, a turn-boundary flag, a DoT, a contest retry, a maintained
 * upkeep, a zone). Conservative: a status whose definition this browser cannot resolve reads as work. */
export function heartbeatEncounterReasons(rows: Row[], read: Pick<ExpiryReaders, 'definition' | 'value'>): string[] {
  const reasons = new Set<string>();
  for (const row of rows) {
    if (isDead(row)) continue;
    for (const mode of ['target', 'caster'] as const) {
      for (const status of statusRows(row, mode)) {
        if (statusPaused(status)) continue;
        if (flag(status, 'status_aoe_source')) reasons.add('paoe');
        const itemId = statusItemId(status);
        if (mode === 'target' && itemId === STATUS_IDS.dying) reasons.add('dying');
        for (const key of ['status_ends_on_target_end_of_turn', 'status_ends_on_target_start_of_turn', 'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn']) if (flag(status, key)) reasons.add('eot_flags');
        if (CAMPAIGN_HANDLED_STATUS_IDS.has(itemId)) continue;
        // A nested definition counts when it carries its identity even with NO layers (a private marker such as the hourly
        // hunger tick compiles to `item_layer_types: []`); only a status with no definition anywhere reads as unknown.
        const nested = record(status.status_arr);
        const def = nested && (record(nested.item_layer_types) || record(nested.items)) ? nested : read.definition(itemId);
        if (!def) { reasons.add('unknown'); continue; }
        if (upper(read.value(def, 'roll_type')) === DOT_ROLL_TYPE) reasons.add('dot');
        const retry = read.value(def, 'target_retries_contesting_roll_on_end_of_turn');
        if (retry != null && retry !== '' && Number(retry) !== 0) reasons.add('contest');
        const maintained = read.value(def, 'maintained_by_ability');
        if (maintained != null && maintained !== '') reasons.add('maintained');
        if (read.value(def, 'persistent_aoe') != null && read.value(def, 'persistent_aoe') !== '') reasons.add('paoe');
      }
    }
  }
  return [...reasons];
}

// (2026-10-01, lane N1b) The old `turnEconomyGates()` lived here: legacy's pass 1 rested every live member's out-of-combat turn
// economy (aps → aps_total, reaction → 1, a Summoner's Ether pinned) before it read statuses, so a roster not already at rest
// handed the push back. Since R-124 the ENCOUNTER PAGE rests the roster itself (janus-engine/economy/out-of-combat.ts +
// websocket/ooc-rest-truth.ts), so the push no longer needs a rested roster — and the gate never cleared for a `no_actions`
// holder (a Stunned creature at aps 0 handed every push back forever). Deleted with its endpoint twin.

/** Remaining calendar/player families outside the native encounter pass. Held PCs carry their deterministic charge
 * ticks; the round planner runs independently even when another family needs a named boundary skip. */
export function heartbeatWorkReasons(hb: HeartbeatEmbedLike, heldPcIds: ReadonlySet<string> = new Set(), hungerCovered = false): string[] {
  const work: string[] = [];
  // Held rows drive expiry/F1 finishes. Crafting uses its separate browser observer;
  // an off-roster scheduling hint cannot discard a held creature's calendar plan.
  if (hb.timed_error) work.push('timed_error');
  if (hb.hunger && !hungerCovered) work.push('hunger'); // T4c-2: covered per held PC by the meter ticks; the endpoint proves every PC with meters
  for (const [pc, rs] of Object.entries(hb.pcs ?? {})) {
    const held = heldPcIds.has(upper(pc));
    const concrete = held && Array.isArray(rs) ? rs.filter(r => r !== 'unknown' && !TICK_COVERED_PC_REASONS.has(r)) : [];
    if (concrete.filter(r => r !== 'regen' && r !== 'awake').length) work.push(`pc:${pc}:${concrete.join('+')}`);
  }
  return work;
}

/** Deterministic pass-3 accrual on one held PC: (no Psi accrual — since 2026-10-16 Psi reads full out of combat, pinned by the encounter
 * page's out-of-combat economy, economy/out-of-combat.ts psiPin); Bard melody (max > 0)
 * pinned to max while Performing a Melody, emptied to 0 otherwise; Wizard mastery pool emptied when not Holding Mastery
 * (held → untouched; an EMPTY pool still carrying the marker → legacy removes the marker: a gate). Charges are read from
 * the row's entries as legacy reads them (get_ability_charges_by_ability_id over the passive_ability bucket for psi / melody,
 * ft_mastery_pool_state over every charge bucket for mastery). */
export function chargeTicksFor(row: Row, entries: AbilityEntry[], read: ReductionReaders): { ticks: ChargeTick[]; gates: string[] } {
  const ticks: ChargeTick[] = []; const gates: string[] = [];
  if (itemTypeOf(row) !== 'player_character' || isDead(row)) return { ticks, gates };
  const id = rowId(row);
  const tick = (kind: ChargeTick['kind'], item_id: string, from: number, requested: number): void => {
    const to = from + chargeReductionDelta(row, requested - from, read);
    if (to !== from) ticks.push({ actor_id: id, kind, item_id, from, to });
  };
  const classes = classIdsOf(row);
  const entry = (itemId: string, passiveOnly: boolean): AbilityEntry | null => entries.find(e => upper(e.id) === itemId && !e.inactive && (!passiveOnly || e.type === 'passive_ability')) ?? null;
  for (const cls of classes) {
    if (cls === CLASS_IDS.bard) {
      const melody = entry(MECHANIC_IDS.melody, true);
      const cur = Number(melody?.charges ?? 0), max = Number(melody?.max ?? 0);
      if (melody && max > 0) {
        const performing = hasStatus(row, STATUS_IDS.performing_a_melody);
        if (performing && cur < max) tick('melody', MECHANIC_IDS.melody, cur, max);
        else if (!performing && cur > 0) tick('melody', MECHANIC_IDS.melody, cur, 0);
      }
    }
    if (cls === CLASS_IDS.wizard) {
      const pool = entry(MECHANIC_IDS.mastery, false);
      if (pool) {
        const cur = Number(pool.charges ?? 0);
        const holding = hasStatus(row, STATUS_IDS.holding_mastery);
        const upkeep = masteryOutOfCombatDecision(cur, holding);
        if (upkeep.removeMarker) gates.push('mastery-marker:' + id);
        else if (upkeep.drain > 0) tick('mastery', MECHANIC_IDS.mastery, cur, cur - upkeep.drain);
      }
    }
  }
  return { ticks, gates };
}

/** Hourly accrual and the resulting hunger status plan for one held PC. `removedIds`
 * are the expiry pass's removals; `startClock` counts the ordinary span's hourly ticks.
 * An omitted startClock retains legacy Fast Forward's single sweep. */
export function hungerTickFor(row: Row, opts: { hungerEnabled: boolean; finalClock: CampaignClockCols; startClock?: CampaignClockCols; calendar: CampaignCalendarEmbed; removedIds: ReadonlySet<string>; marker?: Row; uuid: () => string; nativeConsequences?: boolean; read: Pick<ExpiryReaders, 'value' | 'entries' | 'seconds'> & Partial<Pick<ExpiryReaders, 'allStatusRows' | 'definition'>> }): { tick: MeterTick | null; gate: string | null; thresholds: ThresholdApply[]; zero?: ZeroTransition | null; to_deaths?: ToDeath[]; reapplies?: Reapply[] } {
  const none = { tick: null, gate: null, thresholds: [] as ThresholdApply[] };
  if (!opts.hungerEnabled || itemTypeOf(row) !== 'player_character' || isDead(row)) return none;
  const ec = record(row.encounter_creatures) ?? {};
  const pc = record(row.player_characters);
  if (Number(scalar(pc?.player_character_inactive) ?? 0) !== 0) return none;
  const hunger = Number(scalar(ec.hunger_meter_current) ?? 0), thirst = Number(scalar(ec.thirst_meter_current) ?? 0);
  if (!(hunger > 0) || !(thirst > 0)) return none; // legacy: an empty meter (either) skips the whole tick
  const hungerMax = Number(opts.read.value(row, 'hunger_meter_max') ?? 100), thirstMax = Number(opts.read.value(row, 'thirst_meter_max') ?? 100);
  const immune = new Set((opts.read.entries(row, 'immune_status_effect') as unknown[]).map(e => upper(layerListEntryUuid(e))));
  const has = (id: string): boolean => statusRows(row, 'target').some(s => statusItemId(s) === id && !opts.removedIds.has(upper(scalar(s.status_id))));
  const skipHunger = immune.has(HUNGER_IDS.hungry);
  const skipThirst = immune.has(HUNGER_IDS.thirsty);
  if (skipHunger && skipThirst) return none;
  const id = rowId(row);
  const tickPresent = has(HUNGER_IDS.tick);
  // Reuse the expiry reader for lean end columns. An absent marker first ticks on the
  // first advanced round; an existing marker ticks at its saved end, then hourly.
  const endSeconds = opts.read.seconds(opts.finalClock);
  const startSeconds = opts.startClock ? opts.read.seconds(opts.startClock) : endSeconds;
  const marker = opts.marker ?? statusRows(row, 'target').find(s => statusItemId(s) === HUNGER_IDS.tick);
  const markerEnd = marker ? statusEndSeconds(marker, opts.read) : null;
  const firstTick = opts.startClock ? Math.min(endSeconds, startSeconds + Math.max(TIME_PUSH_ROUND_SECONDS,
    Math.ceil(((markerEnd ?? startSeconds) - startSeconds) / TIME_PUSH_ROUND_SECONDS) * TIME_PUSH_ROUND_SECONDS)) : endSeconds;
  const stepsTo = (meter: 'hunger' | 'thirst', threshold: number): number => {
    const current = meter === 'hunger' ? hunger : thirst, max = meter === 'hunger' ? hungerMax : thirstMax;
    const step = Math.round(max * (meter === 'hunger' ? 0.03 : 0.05));
    return current <= threshold ? 1 : step > 0 ? Math.ceil((current - threshold) / step) : Infinity;
  };
  // The first empty non-immune meter freezes BOTH meters on subsequent ticks.
  // Cap the span at that tick, including its marker and every status onset.
  const availableTicks = tickPresent ? 0 : opts.startClock ? 1 + Math.floor(Math.max(0, endSeconds - firstTick) / HUNGER_TICK_SECONDS) : 1;
  const hourlyTicks = Math.min(availableTicks,
    skipHunger ? Infinity : stepsTo('hunger', 0), skipThirst ? Infinity : stepsTo('thirst', 0));
  let hungerTo = hunger, thirstTo = thirst; let write = false;
  if (hourlyTicks > 0) {
    write = true;
    if (!skipHunger) hungerTo = Math.max(hunger - Math.round(hungerMax * 0.03) * hourlyTicks, 0);
    if (!skipThirst) thirstTo = Math.max(thirst - Math.round(thirstMax * 0.05) * hourlyTicks, 0);
  }
  if (availableTicks > 1 && statusRows(row, 'target').some(s => HUNGER_APPLY_IDS.some(itemId => statusItemId(s) === itemId) && opts.removedIds.has(upper(scalar(s.status_id))))) {
    return { tick: null, gate: 'hourly-status-expiry:' + id, thresholds: [] };
  }
  const eventClock = (meter: 'hunger' | 'thirst', threshold: number): CampaignClockCols => !write || !opts.startClock ? { ...opts.finalClock }
    : ftCampaignTimeSubtractSeconds(opts.finalClock, opts.calendar, endSeconds - firstTick - (Math.min(hourlyTicks, stepsTo(meter, threshold)) - 1) * HUNGER_TICK_SECONDS);
  // Legacy's order per meter (hunger first, then thirst, each seeing what the other already applied): the <= 20 % THRESHOLD (Hungry /
  // Thirsty — T4d), then at zero with Starving / Parched not held: the ZERO chain when Exhausted is not held (T4e: remove the flag,
  // Starving / Parched, Maimed, Exhausted), else the "TO DEATH" branch (T4f: cast the free ability, re-apply
  // Starving / Parched 1 day). A zero chain applies Exhausted, so the other meter reaching zero in the SAME pass takes its "to death"
  // branch (both meters at zero). Ported cases (2026-09-28):
  //  - the flag NOT held at a zero chain → legacy applies it and removes it again in the same pass: `flag_transient`;
  //  - Maimed held at a zero chain → ONE stack onto the held row (`maimed_stack`); the 10th stack (overflow = Dead) stays a gate;
  //  - "to death": the push commits the re-apply (+ the threshold that stays); the adapter casts the ability after the commit.
  const thresholds: ThresholdApply[] = [];
  const toDeaths: ToDeath[] = [];
  const reapplies: Reapply[] = [];
  const apply = (kind: 'hungry' | 'thirsty'): void => { const meter = kind === 'hungry' ? 'hunger' : 'thirst'; thresholds.push({ actor_id: id, kind, status_item_id: HUNGER_IDS[kind], status_id: opts.uuid(), connected_id: opts.uuid(), starts: eventClock(meter, (meter === 'hunger' ? hungerMax : thirstMax) * 0.2) }); };
  const gate = (why: string): { tick: null; gate: string; thresholds: ThresholdApply[] } => ({ tick: null, gate: why + ':' + id, thresholds: [] });
  let zero: ZeroTransition | null = null;
  // Older callers without native readers can prove only an unchanged positive speed.
  // The live planner settles post-change support and listeners in its native aftermath frame.
  const airborneCheck = (applied: readonly string[]): string | null => {
    const pref = upper(scalar(record(row.encounter_creatures)?.preferred_speed_type));
    if (!pref || !AIRBORNE_SPEED_IDS.has(pref)) return null;
    for (const id of applied) {
      const def = opts.read.definition?.(id) ?? null;
      if (!def) return 'airborne-definition-not-held';
      if (opts.nativeConsequences) continue;
      if (Object.keys(record(def.item_layer_types) ?? {}).some(k => /^speed_|^teleport$/.test(k))) return 'airborne-speed-layer';
    }
    if (opts.nativeConsequences) return null;
    // layer_value('speed_<kind>') bridges to the compiled cluster computed.creature.speeds.<kind> (a number or a {total} wrapper — the
    // shared/hunker-visibility.ts speedFeetFromCluster twin); the authored layer only when the cluster cannot answer. The LEAN roster row
    // carries no cluster: the adapter holds the PC's full compile first (time-truth.ts hydrateHungerDefinitions).
    // (Codex P2) the speed is read from the PRE-push compile: a status THIS push removes (pass 2 runs before the hunger pass) that carries a
    // speed layer — or whose definition is not held — may take the flight / swim speed away first → withheld, never guessed
    for (const s of statusRows(row, 'target')) {
      if (!opts.removedIds.has(upper(scalar(s.status_id)))) continue;
      const def = record(s.status_arr) && Object.keys(record(record(s.status_arr)!.item_layer_types) ?? {}).length ? record(s.status_arr) : opts.read.definition?.(statusItemId(s)) ?? null;
      if (!def) return 'airborne-removal-definition-not-held';
      if (Object.keys(record(def.item_layer_types) ?? {}).some(k => /^speed_|^teleport$/.test(k))) return 'airborne-speed-removed';
    }
    const kind = pref === SPEED_FLYING_ID ? 'flying' : 'swimming';
    const slot = record(record(record(row.computed)?.creature)?.speeds)?.[kind];
    const fromCluster = Number(scalar(record(slot) ? record(slot)!.total : slot));
    const speed = Math.trunc(Number.isFinite(fromCluster) && slot !== undefined ? fromCluster : Number(scalar(opts.read.value(row, 'speed_' + kind)) ?? 0) || 0);
    return speed > 0 ? null : 'airborne-grounded';
  };
  const zeroOf = (meter: 'hunger' | 'thirst'): ZeroTransition | string => {
    const flagId = meter === 'hunger' ? HUNGER_IDS.hungry : HUNGER_IDS.thirsty;
    const air = airborneCheck([flagId, meter === 'hunger' ? HUNGER_IDS.starving : HUNGER_IDS.parched, HUNGER_IDS.maimed, HUNGER_IDS.exhausted]);
    if (air) return meter + '-zero-' + air;
    const maimedRows = statusRows(row, 'target').filter(s => statusItemId(s) === HUNGER_IDS.maimed && !opts.removedIds.has(upper(scalar(s.status_id))));
    let maimedStack: { status_id: string; from: number } | null = null;
    if (maimedRows.length > 1) return meter + '-zero-maimed-rows';
    if (maimedRows.length === 1) {
      const from = Number(scalar(maimedRows[0]!.status_stack) ?? 1) || 1;
      if (from >= MAIMED_MAX_STACK) return meter + '-zero-maimed-overflow'; // the 10th maim applies Dead (the death pipeline)
      maimedStack = { status_id: upper(scalar(maimedRows[0]!.status_id)), from };
    }
    const removeIds = statusRows(row, 'target').filter(s => statusItemId(s) === flagId && !opts.removedIds.has(upper(scalar(s.status_id)))).map(s => upper(scalar(s.status_id)));
    // Native loss settlement owns single-connection peers and their caster projections.
    // A partial caller must not save a flag loss without that removal dispatcher.
    const peers = opts.read.allStatusRows ? opts.read.allStatusRows() : [...statusRows(row, 'target'), ...statusRows(row, 'caster')];
    for (const sid of removeIds) {
      const conn = upper(scalar(statusRows(row, 'target').find(s => upper(scalar(s.status_id)) === sid)?.status_connected_id));
      if (!opts.nativeConsequences && conn && peers.some(p => upper(scalar(p.status_id)) !== sid && upper(scalar(p.status_connected_id)) === conn)) return meter + '-zero-removal-readers';
    }
    const first = meter === 'hunger' ? HUNGER_IDS.starving : HUNGER_IDS.parched;
    const applyItems = maimedStack ? [first, HUNGER_IDS.exhausted] : [first, HUNGER_IDS.maimed, HUNGER_IDS.exhausted];
    return { actor_id: id, meter, remove_item_id: flagId, remove_status_ids: removeIds, apply_item_ids: applyItems,
      row_ids: Object.fromEntries([...(removeIds.length === 0 ? [flagId] : []), ...applyItems].map(item => [item, { status_id: opts.uuid(), connected_id: opts.uuid() }])),
      first_ends: ftCampaignTimeAddSeconds(eventClock(meter, 0), opts.calendar, 86400),
      ...(removeIds.length === 0 ? { flag_transient: true as const } : {}), ...(maimedStack ? { maimed_stack: maimedStack } : {}) };
  };
  const branch = (meter: 'hunger' | 'thirst', to: number, max: number): string | null => {
    const flag = meter === 'hunger' ? 'hungry' as const : 'thirsty' as const;
    const first = meter === 'hunger' ? 'starving' as const : 'parched' as const;
    const thresholdDue = max > 0 && to / max <= 0.2 && !has(HUNGER_IDS[flag]) && !has(HUNGER_IDS[first]);
    if (to === 0 && !has(HUNGER_IDS[first])) {
      if (!has(HUNGER_IDS.exhausted) && zero === null) { const z = zeroOf(meter); if (typeof z === 'string') return z; zero = z; return null; }
      // "to death": Exhausted held (or this pass's zero chain applied it) — the threshold stays, the ability is cast, Starving / Parched re-applied
      const air = airborneCheck(thresholdDue ? [HUNGER_IDS[flag], HUNGER_IDS[first]] : [HUNGER_IDS[first]]);
      if (air) return meter + '-to-death-' + air;
      if (thresholdDue) apply(flag);
      toDeaths.push({ actor_id: id, meter, ability_id: TO_DEATH_IDS[meter] });
      const starts = eventClock(meter, 0);
      reapplies.push({ actor_id: id, kind: first, status_item_id: HUNGER_IDS[first], status_id: opts.uuid(), connected_id: opts.uuid(), starts, ends: ftCampaignTimeAddSeconds(starts, opts.calendar, 86400) });
      return null;
    }
    if (thresholdDue) { const air = airborneCheck([HUNGER_IDS[flag]]); if (air) return meter + '-threshold-' + air; apply(flag); }
    return null;
  };
  // Both meters can reach zero only on a tied tick; hunger owns that tick's chain.
  for (const meter of ['hunger', 'thirst'] as const) {
    if (meter === 'hunger' ? skipHunger : skipThirst) continue;
    const g = branch(meter, meter === 'hunger' ? hungerTo : thirstTo, meter === 'hunger' ? hungerMax : thirstMax);
    if (g) return gate(g);
  }
  if (!write) return { tick: null, gate: null, thresholds };
  const starts = opts.startClock ? ftCampaignTimeSubtractSeconds(opts.finalClock, opts.calendar, endSeconds - firstTick - (hourlyTicks - 1) * HUNGER_TICK_SECONDS) : { ...opts.finalClock };
  const ends = ftCampaignTimeAddSeconds(starts, opts.calendar, HUNGER_TICK_SECONDS);
  return { tick: { actor_id: id, hunger_from: hunger, hunger_to: hungerTo, thirst_from: thirst, thirst_to: thirstTo, tick: { status_id: opts.uuid(), connected_id: opts.uuid(), starts, ends } }, gate: null, thresholds, ...(zero ? { zero } : {}), ...(toDeaths.length ? { to_deaths: toDeaths, reapplies } : {}) };
}
/** t4g (R-122, 2026-10-01): the twin of ft_c1_live_status_trigger_hit() — does a carrier the row holds list this status item under a
 * gain (or loss) trigger? janus_trigger() would fire it through a janus() pipeline the time push does not run, so the endpoint refused
 * such a push until R-122; the gate lives HERE now. Walks janus_trigger_carriers() (the layer entries, the raw root slot, the compiled
 * computed.creature.abilities list); a carrier whose definition this page does not hold reads as listening ('definition-not-held'). */
export function statusTriggerLayer(row: Row, statusItemId: string, gain: boolean, read: Pick<ExpiryReaders, 'entries' | 'definition'>): string | null {
  const layers = gain ? ['triggers_when_caster_gains_status_effect', 'triggers_when_target_gains_status_effect'] : ['triggers_when_caster_loses_status_effect', 'triggers_when_target_loses_status_effect'];
  const wanted = upper(statusItemId);
  const lists = (def: Row): string | null => { for (const layer of layers) if (read.entries(def, layer).some(e => upper(layerListEntryUuid(e)) === wanted)) return layer; return null; };
  const abilities = record(record(record(row.computed)?.creature)?.abilities);
  for (const carrier of TRIGGER_CARRIERS) {
    const pool = [...read.entries(row, carrier), ...(Array.isArray(row[carrier]) ? row[carrier] as unknown[] : []), ...(Array.isArray(abilities?.[carrier]) ? abilities![carrier] as unknown[] : [])];
    for (const entry of pool) {
      const e = record(entry);
      if (e && e.passive_conditions_active === false) continue;
      if (e) { const hit = lists(e); if (hit) return hit; }
      const nested = record(e?.item_arr);
      let def: Row | null = nested && record(nested.item_layer_types) ? nested : null;
      if (!def) { const id = upper(layerListEntryUuid(nested ?? entry) || scalar(e?.ability_id)); def = id ? read.definition(id) : null; }
      if (!def) return 'definition-not-held';
      const hit = lists(def); if (hit) return hit;
    }
  }
  return null;
}
/** A zero-layer status definition shell from the page's held catalog (identity only; `item_layer_types` empty) — the endpoint relays it
 * verbatim (R-122: shells come from the browser, never minted in PHP). */
function statusShell(read: Pick<ExpiryReaders, 'definition'>, itemId: string, fallbackName: string): Row {
  const items = record(read.definition(itemId)?.items);
  return { items: { item_id: itemId, item_type: 'status_effect', item_name: String(scalar(items?.item_name) ?? fallbackName) }, item_layer_types: {} };
}
export function resolveTimePush(input: TimePushInput, uuid: () => string): TimePushScope {
  const plan = timePushPlan(input, uuid);
  let step = plan.next();
  while (!step.done) step = plan.next(resolveOocRoundPass(step.value));
  return step.value;
}

/** The ordinary GM push uses the same planner while yielding its expensive round work. */
export async function resolveTimePushAsync(input: TimePushInput, uuid: () => string): Promise<TimePushScope> {
  const plan = timePushPlan(input, uuid);
  let step = plan.next();
  while (!step.done) step = plan.next(await resolveOocRoundPassAsync(step.value));
  return step.value;
}

function* timePushPlan(input: TimePushInput, uuid: () => string): Generator<OocRoundInput, TimePushScope, OocRoundResult> {
  const no = (reason: string, detail?: Record<string, unknown>): TimePushScope => (detail ? { supported: false, reason, detail } : { supported: false, reason });
  if (!input.isGM) return no('permission');
  const hungerOnly = input.hungerOnly === true;
  const rounds = Number(input.rounds);
  if (hungerOnly ? rounds !== 0 : (!Number.isInteger(rounds) || rounds <= 0 || rounds > TIME_PUSH_MAX_ROUNDS)) return no('rounds', { rounds: input.rounds });
  if (input.inCombat === null) return no('combat-unknown');
  if (hungerOnly && input.inCombat === true) return no('in-combat'); // the in-combat clock is the round wrap's
  if (hungerOnly && input.hungerEnabled !== true) return no('hunger-off');
  if (!isClock(input.clock)) return no('clock-snapshot');
  if (!input.calendar) return no('calendar');
  if (![0, 1, 2].includes(input.rollPrivate)) return no('privacy');
  const heldRows = (input.rows ?? []).filter(row => !Number(ecLean(row, 'encounter_creature_deleted') ?? 0));
  const seconds = rounds * TIME_PUSH_ROUND_SECONDS;
  const expected = ftCampaignTimeAddSeconds(input.derivedClock ?? input.clock, input.calendar, seconds);
  const base = {
    schema: TIME_PUSH_SCHEMA, operation_id: uuid(), campaign_id: input.campaignId.toUpperCase(), encounter_id: input.encounterId.toUpperCase(),
    rounds, seconds, expected: { clock: expected }, roll_private: input.rollPrivate, gm_log: input.gmLog,
  } as const;
  let roundPass: ReturnType<typeof resolveOocRoundPass> = null;
  let roundDebt = 0;
  if (!hungerOnly && input.inCombat === false && input.readers) {
    const actualRounds = Math.max(rounds,
      Math.floor(input.readers.seconds(expected) / TIME_PUSH_ROUND_SECONDS) - Math.floor(input.readers.seconds(input.clock) / TIME_PUSH_ROUND_SECONDS));
    const owedRounds = Math.min(TIME_PUSH_MAX_ROUNDS, actualRounds);
    const firstClock = input.fastForward ? ftCampaignTimeSubtractSeconds(expected, input.calendar, owedRounds * TIME_PUSH_ROUND_SECONDS)
      : ftCampaignTimeSubtractSeconds(input.clock, input.calendar, input.clock.second % TIME_PUSH_ROUND_SECONDS);
    roundPass = yield { encounter: input.encounter ?? { encounters: { encounter_id: input.encounterId, in_combat: 0 } },
      rows: heldRows, rounds: owedRounds, fastForward: input.fastForward, clock: firstClock, calendar: input.calendar,
      calendarFacts: input.calendarFacts, weariness: input.weariness, readers: input.readers as EndTurnReaders, readersFor: input.roundReaders, preflightOnly: input.roundPreflightOnly,
      afterRound: input.readers.abilityEntries ? rows => rows.flatMap(row => chargeTicksFor(row, input.readers!.abilityEntries!(row), input.readers!).ticks) : undefined };
    if (!input.fastForward) roundDebt = actualRounds - owedRounds;
  }
  const calendarCommand = (command: TimePushCommand): TimePushScope => {
    if (roundPass) {
      const inventoryPlan = command.tick?.spawnRows;
      command.tick = roundPass.tick;
      if (inventoryPlan) command.tick.spawnRows = mergeSpawnPlans(command.tick.spawnRows, inventoryPlan);
      command.tick_before_ec = roundPass.before;
    }
    if (roundDebt > 0) command.round_debt = roundDebt;
    return { supported: true, command };
  };
  // A gated calendar family has not produced a complete safe plan. Discard its partial writes,
  // advance the clock, and record exactly why the effects were omitted. Never re-run them in PHP.
  const skip = (reason: string, detail?: Record<string, unknown>): TimePushScope => hungerOnly ? no('nothing-due', { reason, ...detail }) : calendarCommand({
    ...base, mode: 'calendar', before: { clock: { ...input.clock! }, running_since: input.runningSince, in_combat: false },
    removals: [], ticks: [], meters: [], frame: { rows: {} }, held_actor_ids: heldRows.map(rowId).filter(Boolean),
    rules_version: TIME_PUSH_RULES_CALENDAR,
    rows: [skipUnported('time-push:' + reason + (detail ? ':' + JSON.stringify(detail) : ''), detail)],
  });
  if (input.inCombat === true) {
    const command: TimePushCommand = { ...base, mode: 'combat', before: { clock: { ...input.clock }, running_since: input.runningSince, in_combat: true }, removals: [], ticks: [], meters: [], frame: { rows: {} }, held_actor_ids: [], rules_version: TIME_PUSH_RULES_COMBAT };
    return { supported: true, command };
  }
  // ---- T4b + T4c: the calendar lane (out of combat) ----
  const hb = input.heartbeat ?? null;
  if (!hb && !hungerOnly) return skip('heartbeat-embed');
  const rows = roundPass?.roster ?? heldRows;
  const originalRead = input.readers ?? null;
  const read = originalRead && withInventoryExpiry({ ...originalRead, rowFor: (id: string) => rows.find(row => rowId(row) === upper(id)) ?? null,
    roster: () => rows, allStatusRows: () => rows.flatMap(row => [...statusRows(row, 'target'), ...statusRows(row, 'caster')]) }, input.encounter ?? {}, rows);
  if (!read) return skip('readers');
  const nowSeconds = read.seconds(input.derivedClock ?? input.clock);
  const heldIds = rows.map(rowId).filter(id => id !== '');
  const heldPcs = new Set(rows.filter(r => itemTypeOf(r) === 'player_character' && !isDead(r)).map(rowId));
  if (!hungerOnly && hb) {
    const work = heartbeatWorkReasons(hb, heldPcs, input.hungerEnabled === true);
    if (work.length) return skip('heartbeat-work', { reasons: work, held_pcs: [...heldPcs] });
    // A caller without the native calendar API cannot retire a special handler as a plain status.
    // Current pages supply it; old/partial callers retain the precise due effect as a named skip.
    if (!input.readers?.tick) for (const row of rows) for (const status of statusRows(row, 'target')) {
      const itemId = statusItemId(status), end = statusEndSeconds(status, read);
      if (SPECIAL_STATUS_IDS.has(itemId) && !statusPaused(status) && end !== null && nowSeconds + seconds >= end)
        return skip('expiry-gate', { gates: ['special-status:' + itemId] });
    }
  }
  // The removals at the FINAL clock: the derived now (what the tab shows) + the span. Since R-122 the endpoint writes this list as
  // decided here (no server re-derivation). A hunger heartbeat removes ONLY the hourly tick rows that ran out (the rest of pass 2 is the
  // legacy checkpoint replay's): the expiry walk runs over a view of the rows that carries nothing else.
  const tickOnly = (row: Row): Row => {
    const statuses = record(row.statuses) ?? {};
    const keep = (list: unknown): unknown[] => (Array.isArray(list) ? list : []).filter(s => statusItemId(record(s) ?? {}) === HUNGER_IDS.tick);
    return { ...row, statuses: { ...statuses, target: keep(statuses.target), caster: keep(statuses.caster) } };
  };
  const retiredInventory = new Set((roundPass?.tick.spawnRows?.writes ?? []).filter(w => w.table === 'statuses' && w.row.status_deleted === 1).map(w => upper(w.row.status_id)));
  const result = expirableStatuses(hungerOnly ? rows.map(tickOnly) : rows, { kind: 'calendar' },
    { ...read, ...(hungerOnly ? { inventoryRows: () => [] } : {}), nowSeconds: nowSeconds + seconds }, retiredInventory);
  if (result.gates.length) return skip('expiry-gate', { gates: result.gates });
  const frameRows: Record<string, TimePushRowFrame> = {};
  const rowFrame = (id: string): TimePushRowFrame => (frameRows[id] ??= { status_remove_ids: [], skills: {}, charges: [] });
  const removals: StatusRemoval[] = [];
  const byLoser = new Map<string, string[]>();
  const inventoryPlan = inventoryExpiryPlan(result.removals, read);
  for (const r of result.removals) {
    if (inventoryPlan.writes.some(w => upper(w.row.status_id) === r.status_id)) continue;
    removals.push({ ...r, log_by: r.caster_id || r.target_id }); byLoser.set(r.target_id, [...(byLoser.get(r.target_id) ?? []), r.status_id]);
  }
  for (const [loserId, ids] of byLoser) {
    const loser = read.rowFor(loserId);
    if (!loser) return skip('expiry-gate', { gates: ['status-loser-row-missing'] });
    const reversal = skillsWithoutStatuses(loser, ids);
    if (reversal.netZeroWithLines.length) return skip('expiry-gate', { gates: ['status-skill-net-zero'] });
    const f = rowFrame(loserId); f.status_remove_ids = ids; f.skills = reversal.skills;
    const budget = [...removals].reverse().find(r => r.target_id === loserId && r.attack_budget)?.attack_budget;
    if (budget) f.attack_budget = budget;
  }
  // Native rounds already accrued held class pools after upkeep. Without a native pass,
  // retain the calendar lane's held-PC charge plan. Hunger heartbeats do not accrue pools.
  const ticks: ChargeTick[] = [];
  if (!read.abilityEntries) return skip('readers');
  if (!hungerOnly && !roundPass) for (const row of rows) {
    const t = chargeTicksFor(row, read.abilityEntries(row), read);
    if (t.gates.length) return skip('heartbeat-work', { reasons: t.gates });
    for (const tick of t.ticks) { ticks.push(tick); rowFrame(tick.actor_id).charges.push({ item_id: tick.item_id, current: tick.to }); }
  }
  // Count the held PCs' hourly meter ticks and decide their supported status outcomes.
  const meters: MeterTick[] = [];
  const thresholds: ThresholdApply[] = [];
  const zeros: ZeroTransition[] = [];
  const toDeaths: ToDeath[] = [];
  const reapplies: Reapply[] = [];
  const hungerPlans: HungerConsequencePlan[] = [];
  const withheld: string[] = [];
  if (input.hungerEnabled === true) {
    if (!input.derivedClock || !isClock(input.derivedClock)) return skip('hunger-clock-snapshot');
    const finalClock = ftCampaignTimeAddSeconds(input.derivedClock, input.calendar, seconds);
    const removedIds = new Set([...removals.map(r => r.status_id), ...(roundPass?.tick.statusesRemoved.map(r => r.status_id ?? '') ?? [])]);
    for (const row of rows) {
      const preWithheld = input.withheldHunger?.[rowId(row)];
      if (preWithheld) { withheld.push(preWithheld); continue; }
      // The native pass may already have retired the old marker. Keep its original end for hourly counting.
      const marker = statusRows(heldRows.find(r => rowId(r) === rowId(row)) ?? row, 'target').find(s => statusItemId(s) === HUNGER_IDS.tick);
      const h = hungerTickFor(row, { hungerEnabled: true, finalClock,
        startClock: input.fastForward || hungerOnly ? undefined : input.derivedClock,
        marker, calendar: input.calendar, removedIds, uuid, read, nativeConsequences: !!read.tick });
      // (Codex review 2026-10-01) a PC-scoped hunger gate WITHHOLDS that PC's hunger step only: the legacy sweep no longer decides hunger
      // (R-123), so handing the whole push back would also starve every other PC of its tick. Nothing of the withheld PC's hunger is written.
      if (h.gate) { withheld.push(h.gate); continue; }
      // A reader set without a native dispatcher still names the exact listening arm.
      const listens = (itemId: string, gain: boolean): string | null => statusTriggerLayer(row, itemId, gain, read);
      let trig = false;
      for (const g of [...h.thresholds.map(th => th.status_item_id), ...(h.reapplies ?? []).map(ra => ra.status_item_id)]) {
        const hit = listens(g, true);
        if (hit && !read.tick) { withheld.push('threshold-trigger:' + rowId(row) + ':' + hit); trig = true; break; }
      }
      if (trig) continue;
      if (h.zero?.flag_transient) for (const gain of [true, false]) {
        const hit = listens(h.zero.remove_item_id, gain);
        if (hit && !read.tick) { withheld.push('hunger-zero-flag-trigger:' + rowId(row) + ':' + hit); trig = true; break; }
      }
      if (trig) continue;
      if (h.zero) zeros.push(h.zero);
      for (const th of h.thresholds) {
        thresholds.push(th);
        const f = rowFrame(th.actor_id);
        f.statuses_upsert = [...(f.statuses_upsert ?? []), { status_id: th.status_id, status_target_id: th.actor_id, status_caster_id: th.actor_id, status_connected_id: th.connected_id, status_item_id: th.status_item_id, status_stack: 1,
          status_arr: statusShell(read, th.status_item_id, th.kind === 'hungry' ? 'Hungry' : 'Thirsty'),
          status_starts_campaign_year: th.starts.year, status_starts_campaign_month: th.starts.month, status_starts_campaign_day: th.starts.day, status_starts_campaign_hour: th.starts.hour, status_starts_campaign_minute: th.starts.minute, status_starts_campaign_second: th.starts.second }];
      }
      // Owed casts follow the commit; Starving / Parched ends one day after its hourly onset.
      for (const td of h.to_deaths ?? []) toDeaths.push(td);
      for (const ra of h.reapplies ?? []) {
        reapplies.push(ra);
        const f = rowFrame(ra.actor_id);
        f.statuses_upsert = [...(f.statuses_upsert ?? []), { status_id: ra.status_id, status_target_id: ra.actor_id, status_caster_id: ra.actor_id, status_connected_id: ra.connected_id, status_item_id: ra.status_item_id, status_stack: 1,
          status_arr: statusShell(read, ra.status_item_id, ra.kind === 'starving' ? 'Starving' : 'Parched'),
          status_starts_campaign_year: ra.starts.year, status_starts_campaign_month: ra.starts.month, status_starts_campaign_day: ra.starts.day, status_starts_campaign_hour: ra.starts.hour, status_starts_campaign_minute: ra.starts.minute, status_starts_campaign_second: ra.starts.second,
          status_ends_campaign_year: ra.ends.year, status_ends_campaign_month: ra.ends.month, status_ends_campaign_day: ra.ends.day, status_ends_campaign_hour: ra.ends.hour, status_ends_campaign_minute: ra.ends.minute, status_ends_campaign_second: ra.ends.second }];
      }
      {
        const events: HungerStatusEvent[] = [];
        const entry = (itemId: string, statusId: string, connection: string, ends?: CampaignClockCols): Row => ({
          status_id: statusId, status_item_id: itemId, status_target_id: rowId(row), status_caster_id: rowId(row), status_connected_id: connection, status_stack: 1,
          ...(ends ? Object.fromEntries(Object.entries(ends).map(([key, value]) => ['status_ends_campaign_' + key, value])) : {}) });
        for (const meter of ['hunger', 'thirst'] as const) {
          for (const th of h.thresholds.filter(th => th.kind === (meter === 'hunger' ? 'hungry' : 'thirsty')))
            events.push({ kind: 'gain', status: entry(th.status_item_id, th.status_id, th.connected_id) });
          const z = h.zero?.meter === meter ? h.zero : null;
          if (z) {
            if (z.flag_transient) { const ids = z.row_ids![z.remove_item_id]!; const status = entry(z.remove_item_id, ids.status_id, ids.connected_id);
              events.push({ kind: 'gain', status }, { kind: 'loss', status }); }
            else for (const id of z.remove_status_ids) events.push({ kind: 'loss', status: statusRows(row, 'target').find(s => upper(scalar(s.status_id)) === id)! });
            for (const [index, itemId] of z.apply_item_ids.entries()) {
              const ids = z.row_ids![itemId]!;
              events.push({ kind: 'gain', status: entry(itemId, ids.status_id, ids.connected_id, index === 0 ? z.first_ends : undefined) });
              if (index === 0 && z.maimed_stack) { const held = statusRows(row, 'target').find(s => upper(scalar(s.status_id)) === z.maimed_stack!.status_id)!;
                events.push({ kind: 'stack', status: { ...held, status_stack: z.maimed_stack.from + 1 } }); }
            }
          }
          for (const ra of (h.reapplies ?? []).filter(ra => ra.kind === (meter === 'hunger' ? 'starving' : 'parched')))
            events.push({ kind: 'gain', status: entry(ra.status_item_id, ra.status_id, ra.connected_id, ra.ends) });
        }
        if (events.length) hungerPlans.push({ actorId: rowId(row), events });
      }
      if (!h.tick) continue;
      meters.push(h.tick);
      const f = rowFrame(h.tick.actor_id);
      f.meters = { hunger_meter_current: h.tick.hunger_to, thirst_meter_current: h.tick.thirst_to };
      const t = h.tick.tick;
      f.statuses_upsert = [...(f.statuses_upsert ?? []), { status_id: t.status_id, status_target_id: h.tick.actor_id, status_connected_id: t.connected_id, status_item_id: HUNGER_IDS.tick, status_stack: 1, status_private: 1, status_arr: statusShell(read, HUNGER_IDS.tick, 'Hunger and Thirst Tick'),
        status_starts_campaign_year: t.starts.year, status_starts_campaign_month: t.starts.month, status_starts_campaign_day: t.starts.day, status_starts_campaign_hour: t.starts.hour, status_starts_campaign_minute: t.starts.minute, status_starts_campaign_second: t.starts.second,
        status_ends_campaign_year: t.ends.year, status_ends_campaign_month: t.ends.month, status_ends_campaign_day: t.ends.day, status_ends_campaign_hour: t.ends.hour, status_ends_campaign_minute: t.ends.minute, status_ends_campaign_second: t.ends.second }];

    }
  }
  // A hunger heartbeat that decides nothing posts nothing (an expired tick alone is the replay's to sweep, with the rest of pass 2).
  if (hungerOnly && !meters.length && !thresholds.length && !zeros.length && !toDeaths.length) return no('nothing-due', { withheld });
  const command: TimePushCommand = {
    ...base, mode: 'calendar', before: { clock: { ...input.clock }, running_since: input.runningSince, in_combat: false },
    removals, ticks, meters, frame: { rows: frameRows }, held_actor_ids: heldIds, rules_version: TIME_PUSH_RULES_CALENDAR,
    ...(inventoryPlan.writes.length ? { tick: { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusesChanged: [], charges: [], spawnRows: inventoryPlan } } : {}),
    ...(thresholds.length ? { thresholds } : {}),
    ...(zeros.length ? { zeros } : {}),
    ...(toDeaths.length ? { to_deaths: toDeaths, reapplies } : {}),
    ...(withheld.length ? { rows: withheld.map(reason => skipUnported('time-push:hunger:' + reason)) } : {}),
  };
  let plans = hungerPlans;
  const withhold = (id: string, reason: string): void => {
    withheld.push(reason);
    plans = plans.filter(p => p.actorId !== id);
    command.meters = command.meters.filter(m => m.actor_id !== id);
    command.thresholds = command.thresholds?.filter(t => t.actor_id !== id);
    command.zeros = command.zeros?.filter(z => z.actor_id !== id);
    command.reapplies = command.reapplies?.filter(r => r.actor_id !== id);
    command.to_deaths = command.to_deaths?.filter(t => t.actor_id !== id);
    const f = command.frame.rows[id];
    if (f) { delete f.meters; delete f.statuses_upsert; delete f.status_projection;
      if (!f.status_remove_ids.length && !f.charges.length && !Object.keys(f.skills).length) delete command.frame.rows[id]; }
  };
  // Prove the affected skill/root deltas before a consequence can draw. A missing projection withholds only that due PC.
  for (const plan of plans) {
    const member = rows.find(row => rowId(row) === plan.actorId);
    if (!member) continue;
    try { command.frame.rows[plan.actorId]!.status_projection = timeStatusProjection(member, plan, read.definition); }
    catch (error) { withhold(plan.actorId, 'hunger-projection:' + plan.actorId + ':' + String(error instanceof Error ? error.message : error)); }
  }
  if (read.tick && plans.length) {
    while (plans.length) {
      const native = resolveHungerConsequences({ encounter: input.encounter ?? {}, rows, plans,
        primarySkills: id => command.frame.rows[id]?.status_projection?.skills,
        removedIds: new Set(removals.map(r => r.status_id)), meters: command.meters,
        read: read.tick, entries: read.abilityEntries, rollPrivate: input.rollPrivate, preflightOnly: true });
      if (native.supported) {
        const actual = input.roundPreflightOnly === true ? native : resolveHungerConsequences({ encounter: input.encounter ?? {}, rows, plans,
          primarySkills: id => command.frame.rows[id]?.status_projection?.skills,
          removedIds: new Set(removals.map(r => r.status_id)), meters: command.meters,
          read: read.tick, entries: read.abilityEntries, rollPrivate: input.rollPrivate, preflightOnly: false });
        if (!actual.supported) throw new Error('hunger consequence changed after preflight: ' + actual.reason);
        command.hunger_tick = actual.tick; command.hunger_tick_before_ec = actual.before;
        break;
      }
      const id = native.actorId;
      if (native.reason.includes('trigger-consent')) throw new HandBack(native.reason);
      withhold(id, 'hunger-consequence:' + id + ':' + native.reason);
    }
  }
  if (hungerOnly && !command.meters.length && !command.thresholds?.length && !command.zeros?.length && !command.to_deaths?.length)
    return no('nothing-due', { withheld });
  if (withheld.length) command.rows = withheld.map(reason => {
    const member = rows.find(row => reason.includes(rowId(row)));
    const named = member ? reason.replace(rowId(member), String(ecLean(member, 'encounter_creature_name') ?? itemField(member, 'item_name') ?? 'Creature')) : reason;
    return skipUnported('time-push:hunger:' + named);
  });
  calendarCommand(command);
  command.time_save = timeSavePlan(command, hungerPlans.filter(p => command.meters.some(m => m.actor_id === p.actorId)), read.definition, id => rows.find(row => rowId(row) === id) ?? null);
  return withheld.length ? { supported: true, command, hunger_withheld: withheld } : { supported: true, command };
}
